import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import JSZip from "jszip";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

export default function PdfToImages({ defaultFormat = "png" }) {
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState(defaultFormat); // "png" | "jpg"
  const [scale, setScale] = useState(2);

  const [loading, setLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);

  const [progress, setProgress] = useState(null); // { current, total, label }
  const [pages, setPages] = useState([]); // [{ pageNum, url, filename }]
  const [pageCount, setPageCount] = useState(null); // nombre de pages PDF

  const urlsRef = useRef([]);

  const mime = format === "jpg" ? "image/jpeg" : "image/png";
  const ext = format === "jpg" ? "jpg" : "png";

  const canConvert = useMemo(
    () => !!file && !loading && !zipLoading,
    [file, loading, zipLoading]
  );

  const showZip = useMemo(() => {
    // on n'affiche ZIP que si on connaît le nombre de pages et qu'il y en a 2+
    return !!file && typeof pageCount === "number" && pageCount >= 2;
  }, [file, pageCount]);

  const cleanupUrls = () => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
  };

  const onPick = (f) => {
    cleanupUrls();
    setPages([]);
    setProgress(null);
    setFile(f || null);
    setPageCount(null);
  };

  // Dès qu'un PDF est choisi, on lit son nombre de pages (sans convertir)
  useEffect(() => {
    let cancelled = false;

    async function detectPages() {
      if (!file) return;

      try {
        const buf = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (!cancelled) setPageCount(pdf.numPages);
      } catch (e) {
        console.error(e);
        if (!cancelled) setPageCount(null);
      }
    }

    detectPages();
    return () => {
      cancelled = true;
    };
  }, [file]);

  const renderPageToBlob = async (pdf, pageNum) => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { alpha: false });

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const quality = format === "jpg" ? 0.92 : undefined;

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob a échoué"))),
        mime,
        quality
      );
    });

    return blob;
  };

  const convertAndList = async () => {
    if (!file) return;

    setLoading(true);
    setProgress(null);
    cleanupUrls();
    setPages([]);

    try {
      const buf = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

      // sécurise pageCount si pas déjà set
      setPageCount(pdf.numPages);

      const out = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        setProgress({
          current: pageNum,
          total: pdf.numPages,
          label: "Conversion...",
        });

        const blob = await renderPageToBlob(pdf, pageNum);
        const url = URL.createObjectURL(blob);
        urlsRef.current.push(url);

        const filename = `${baseName(file.name)}-page-${pageNum}.${ext}`;
        out.push({ pageNum, url, filename });
      }

      setPages(out);
      setProgress(null);
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la conversion PDF → images.");
      setProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadZip = async () => {
    if (!file) return;

    setZipLoading(true);
    setProgress(null);

    try {
      const buf = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

      const zip = new JSZip();
      const folder = zip.folder(`${baseName(file.name)}-${ext}`) || zip;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        setProgress({
          current: pageNum,
          total: pdf.numPages,
          label: "Création du ZIP...",
        });

        const blob = await renderPageToBlob(pdf, pageNum);
        const filename = `${baseName(file.name)}-page-${pageNum}.${ext}`;
        folder.file(filename, blob);
      }

      setProgress({ current: 0, total: 0, label: "Compression ZIP..." });

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const zipUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = zipUrl;
      a.download = `${baseName(file.name)}-convert.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(zipUrl), 2000);
      setProgress(null);
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la création du ZIP.");
      setProgress(null);
    } finally {
      setZipLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <h2>PDF → {format.toUpperCase()}</h2>
      <small>Tout se fait dans ton navigateur (aucun envoi)</small>

      <input
        type="file"
        accept=".pdf,application/pdf"
        onChange={(e) => onPick(e.target.files?.[0])}
        disabled={loading || zipLoading}
      />

      <div style={{ maxWidth: 460, margin: "12px auto", textAlign: "left" }}>
        <label style={{ display: "block", marginBottom: 10 }}>
          Format
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={loading || zipLoading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          >
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
          </select>
        </label>

        <label style={{ display: "block" }}>
          Qualité (scale)
          <input
            type="number"
            min={1}
            max={4}
            step={0.5}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            disabled={loading || zipLoading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          />
          <small style={{ color: "#666" }}>
            2 = bon compromis, 3+ = plus lourd
          </small>
        </label>

        {typeof pageCount === "number" && (
          <p style={{ marginTop: 10, color: "#666" }}>
            Pages détectées : <b>{pageCount}</b>
          </p>
        )}
      </div>

      {/* ✅ Boutons alignés (même largeur) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 14,
        }}
      >
        <button
          onClick={convertAndList}
          disabled={!canConvert}
          style={{ minWidth: 180 }}
        >
          {loading ? "Conversion..." : "Convertir"}
        </button>

        {showZip && (
          <button
            onClick={downloadZip}
            disabled={!file || loading || zipLoading}
            style={{ minWidth: 180 }}
          >
            {zipLoading ? "ZIP..." : "Tout télécharger (ZIP)"}
          </button>
        )}
      </div>

      {progress?.label && (
        <p style={{ textAlign: "center", marginTop: 12, color: "#666" }}>
          {progress.label}{" "}
          {progress.total ? (
            <b>
              {progress.current}/{progress.total}
            </b>
          ) : null}
        </p>
      )}

      <div style={{ textAlign: "center", marginTop: 18 }}>
        {pages.map((p) => (
          <div key={p.pageNum} style={{ marginBottom: 8 }}>
            <a href={p.url} download={p.filename}>
              Télécharger {p.filename}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
